import { expected } from '@aloft/toolkit-shared';
import { Block, Flex, Tabs, Typography } from '@aloft/toolkit-sui';

import { useMemo, useState } from 'react';

import { useActiveOrderTabState } from '../../hooks/useActiveOrderTabState.ts';
import { Tkv2OrderManagementDeps } from '../../types.ts';
import { Tkv2OrderCard } from '../OrdersList/Tkv2OrderCard.tsx';
import { CreateCraftsmanOrderModal } from './CreateCraftsmanOrderModal.tsx';
// eslint-disable-next-line aloft/prefer-sound-ui-button
import { CreateOrderButton } from './CreateOrderButton.tsx';
import { Tkv2OrderTableHeader } from './Tkv2OrderTableHeader.tsx';
import { TkOrderListfield, TkOrderListing, Tkv2SortState } from './types.ts';
import { sortTkOrderListings } from './utils/sortTkOrderListings.ts';

type Props = {
  orders: TkOrderListing[];
  getOpenOrderRoute: Tkv2OrderManagementDeps['getOpenOrderRoute'];
  isViewingArchived: boolean;
  isViewingTraining: boolean;
  isViewingCompleted: boolean;
};

export const Tkv2OrderTable = ({
  orders,
  getOpenOrderRoute,
  isViewingArchived,
  isViewingTraining,
  isViewingCompleted,
}: Props) => {
  const [activeTab, setActiveTab] = useActiveOrderTabState();
  const [createOrderOpen, setCreateOrderOpen] = useState<boolean>(false);
  const [sortState, setSortState] = useState<Tkv2SortState>({
    field: 'effectiveDate',
    dir: 'desc',
  });

  const sortedOrders = useMemo(() => sortTkOrderListings(orders, sortState), [orders, sortState]);

  const handleHeaderClick = (field: TkOrderListfield) => {
    setSortState((prevState) => {
      let dir: Tkv2SortState['dir'] = 'asc';
      if (prevState.field === field && prevState.dir === 'asc') {
        dir = 'desc';
      }

      return {
        field,
        dir,
      };
    });
  };

  const orderListIsEmpty = sortedOrders.length === 0 && !isViewingArchived;
  const archivedListIsEmpty = sortedOrders.length === 0 && isViewingArchived;

  return (
    <Flex className="tkv2-order-table">
      <Flex paddingLeft="3rem" paddingBottom="1rem" backgroundColor="surface1">
        <Tabs
          variant="secondary"
          tabs={[
            { label: 'Active', value: 'active' },
            { label: 'Completed', value: 'completed' },
            { label: 'Archived', value: 'archived' },
            { label: 'Training', value: 'training' },
          ]}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />
      </Flex>
      {isViewingArchived === false && isViewingTraining === false && isViewingCompleted === false && (
        <CreateOrderButton
          isTooltipAlwaysOpen={sortedOrders.length === 0}
          onClick={() => setCreateOrderOpen(true)}
        />
      )}
      <Tkv2OrderTableHeader sortState={sortState} handleHeaderClick={handleHeaderClick} />
      <Flex id="orders-list" data-testid="orders-list" className="orders-list">
        {sortedOrders.length > 0 &&
          sortedOrders.map((order) => (
            <Tkv2OrderCard
              key={order.id}
              order={order}
              to={getOpenOrderRoute(
                order.type,
                order.requiresPrefill || false,
                expected(order.id),
                order?.inspectifyOrder || false,
              )}
              updateOrder={() => order.toggleArchive(!order.isArchived)}
              deleteOrder={order.deleteOrder}
              downloadReport={order.downloadReport}
              openDocumentsPage={order.openDocumentsPage}
            />
          ))}

        {orderListIsEmpty && (
          <Flex className="empty-orders-list">
            <Typography variant="h1" __dangerDoNotUseSx={{ fontSize: '60px' }}>
              Welcome to Appraiser Toolkit!
            </Typography>
            <Typography
              variant="h2"
              color="textTertiary"
              __dangerDoNotUseSx={{ fontSize: '20px', fontWeight: 500 }}
            >
              Support your adjustments with cost and market data
            </Typography>
          </Flex>
        )}

        {archivedListIsEmpty && (
          <Block className="empty-archived-orders-list">
            <Typography>You have no archived orders.</Typography>
          </Block>
        )}
      </Flex>
      <CreateCraftsmanOrderModal
        isOpen={createOrderOpen}
        onClose={() => setCreateOrderOpen(false)}
      />
    </Flex>
  );
};
